import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Camera, Plus, Trash, FilePdf, Sparkle, FloppyDisk, GraduationCap, ArrowLeft,
  ShareNetwork, LinkedinLogo, XLogo, FacebookLogo, InstagramLogo, YoutubeLogo, GithubLogo, Globe,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";

const NATURE_CONSENTS = [
  { key: "nature_share", label: "Share my profile with NaturePulse & FarmersPulse", desc: "Let the citizen-science network invite me to Field Tasks and count my observations toward my school/chapter." },
  { key: "public_leaderboard", label: "Show me on public leaderboards", desc: "Display my contributions on institution leaderboards and impact pages." },
  { key: "contact_allowed", label: "Allow the partner to contact me", desc: "Receive task invites and drive updates from NaturePulse." },
];

const emptyEdu = { institution: "", campus: "", department: "", course: "", class_name: "", batch: "", start_year: "", end_year: "", degree: "" };

const SOCIAL_FIELDS = [
  { key: "linkedin", label: "LinkedIn", Icon: LinkedinLogo, ph: "https://linkedin.com/in/you" },
  { key: "twitter", label: "X (Twitter)", Icon: XLogo, ph: "https://x.com/you" },
  { key: "facebook", label: "Facebook", Icon: FacebookLogo, ph: "https://facebook.com/you" },
  { key: "instagram", label: "Instagram", Icon: InstagramLogo, ph: "https://instagram.com/you" },
  { key: "youtube", label: "YouTube", Icon: YoutubeLogo, ph: "https://youtube.com/@you" },
  { key: "github", label: "GitHub", Icon: GithubLogo, ph: "https://github.com/you" },
  { key: "website", label: "Website", Icon: Globe, ph: "https://yoursite.com" },
];

export default function ProfileEdit() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const avatarRef = useRef();
  const pdfRef = useRef();

  useEffect(() => { api.get("/profiles/me").then((r) => setP({ ...r.data, skills: r.data.skills || [], achievements: r.data.achievements || [], education: r.data.education || [], nature_consent: r.data.nature_consent || {} })); }, []);
  if (!p) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;

  const set = (k, v) => setP({ ...p, [k]: v });
  const setNature = (k, v) => setP({ ...p, nature_consent: { ...(p.nature_consent || {}), [k]: v } });
  const setEdu = (i, k, v) => { const e = [...p.education]; e[i] = { ...e[i], [k]: v }; set("education", e); };

  const uploadAvatar = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("avatar_file_id", data.id);
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const importPdf = async (file) => {
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profiles/import-linkedin", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setP((prev) => ({
        ...prev,
        headline: data.headline || prev.headline,
        bio: data.bio || prev.bio,
        location: data.location || prev.location,
        skills: [...new Set([...(prev.skills || []), ...(data.skills || [])])],
        achievements: [...new Set([...(prev.achievements || []), ...(data.achievements || [])])],
        education: [...(prev.education || []), ...((data.education || []).map((e) => ({ ...emptyEdu, ...e })))],
      }));
      toast.success("LinkedIn data imported — review and save.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally { setImporting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: p.name, headline: p.headline, bio: p.bio, location: p.location, phone: p.phone,
        avatar_file_id: p.avatar_file_id, skills: p.skills.filter(Boolean), achievements: p.achievements.filter(Boolean),
        education: p.education.filter((e) => e.institution),
        social: Object.fromEntries(Object.entries(p.social || {}).filter(([, v]) => (v || "").trim())),
        nature_consent: {
          nature_share: !!(p.nature_consent || {}).nature_share,
          public_leaderboard: !!(p.nature_consent || {}).public_leaderboard,
          contact_allowed: !!(p.nature_consent || {}).contact_allowed,
        },
      };
      await api.put("/profiles/me", payload);
      setUser({ ...user, name: p.name, avatar_file_id: p.avatar_file_id });
      toast.success("Profile saved");
      nav("/profile");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  const initials = (p.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");

  return (
    <main className="mx-auto max-w-3xl px-5 py-8">
      <button onClick={() => nav(-1)} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Back</button>
      <h1 className="font-heading text-3xl font-light tracking-tight">Edit your profile</h1>

      {/* LinkedIn import */}
      <Card className="mt-6 border-secondary/30 bg-secondary/5 p-6 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <span className="grid h-12 w-12 place-items-center rounded-xl bg-secondary text-secondary-foreground"><FilePdf size={24} weight="duotone" /></span>
          <div className="flex-1">
            <h2 className="flex items-center gap-1.5 font-heading text-lg font-medium"><Sparkle className="text-secondary" size={16} weight="fill" /> Import from LinkedIn PDF</h2>
            <p className="text-sm text-muted-foreground">Upload your LinkedIn profile PDF export — AI pre-fills your fields. Review before saving.</p>
          </div>
          <input ref={pdfRef} type="file" accept="application/pdf" hidden onChange={(e) => e.target.files[0] && importPdf(e.target.files[0])} data-testid="linkedin-file-input" />
          <Button variant="outline" className="rounded-full gap-2" disabled={importing} onClick={() => pdfRef.current.click()} data-testid="import-linkedin-btn">
            {importing ? "Parsing…" : "Upload PDF"}
          </Button>
        </div>
      </Card>

      {/* Basic info */}
      <Card className="mt-6 border-border/60 p-6 shadow-sm">
        <div className="flex items-center gap-5">
          <div className="relative">
            <Avatar className="h-20 w-20"><AvatarImage src={fileUrl(p.avatar_file_id)} /><AvatarFallback className="bg-accent text-xl">{initials}</AvatarFallback></Avatar>
            <button onClick={() => avatarRef.current.click()} className="absolute -bottom-1 -right-1 grid h-8 w-8 place-items-center rounded-full bg-secondary text-secondary-foreground shadow" data-testid="avatar-upload-btn">
              <Camera size={15} />
            </button>
            <input ref={avatarRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && uploadAvatar(e.target.files[0])} />
          </div>
          <p className="text-sm text-muted-foreground">{uploading ? "Uploading…" : "Add a profile photo so classmates recognise you."}</p>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label>Full name</Label><Input value={p.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="edit-name" /></div>
          <div className="space-y-2"><Label>Location</Label><Input value={p.location || ""} onChange={(e) => set("location", e.target.value)} data-testid="edit-location" /></div>
          <div className="space-y-2 sm:col-span-2"><Label>Headline</Label><Input value={p.headline || ""} onChange={(e) => set("headline", e.target.value)} placeholder="Software Engineer • Class of 2015" data-testid="edit-headline" /></div>
          <div className="space-y-2"><Label>Phone</Label><Input value={p.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
          <div className="space-y-2 sm:col-span-2"><Label>Bio</Label><Textarea rows={3} value={p.bio || ""} onChange={(e) => set("bio", e.target.value)} data-testid="edit-bio" /></div>
          <div className="space-y-2"><Label>Skills (comma separated)</Label><Input value={(p.skills || []).join(", ")} onChange={(e) => set("skills", e.target.value.split(",").map((s) => s.trim()))} /></div>
          <div className="space-y-2"><Label>Achievements (comma separated)</Label><Input value={(p.achievements || []).join(", ")} onChange={(e) => set("achievements", e.target.value.split(",").map((s) => s.trim()))} /></div>
        </div>
      </Card>

      {/* Social & links */}
      <Card className="mt-6 border-border/60 p-6 shadow-sm" data-testid="social-card">
        <h2 className="mb-1 flex items-center gap-2 font-heading text-lg font-medium"><ShareNetwork className="text-secondary" size={20} weight="duotone" /> Social &amp; links</h2>
        <p className="mb-4 text-sm text-muted-foreground">Add your profiles so classmates can follow and connect with you. These show on your public profile.</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {SOCIAL_FIELDS.map(({ key, label, Icon, ph }) => (
            <div key={key} className="space-y-2">
              <Label className="flex items-center gap-1.5"><Icon size={15} weight="fill" className="text-secondary" /> {label}</Label>
              <Input value={(p.social || {})[key] || ""} onChange={(e) => setP({ ...p, social: { ...(p.social || {}), [key]: e.target.value } })} placeholder={ph} data-testid={`social-${key}`} />
            </div>
          ))}
        </div>
      </Card>

      {/* Education */}
      <Card className="mt-6 border-border/60 p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-heading text-lg font-medium"><GraduationCap className="text-secondary" size={20} weight="duotone" /> Education</h2>
          <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => set("education", [...p.education, { ...emptyEdu }])} data-testid="add-education-btn"><Plus size={15} /> Add</Button>
        </div>
        <div className="space-y-4">
          {p.education.length === 0 && <p className="text-sm text-muted-foreground">No institutions yet. Add your first one.</p>}
          {p.education.map((e, i) => (
            <div key={i} className="rounded-xl border border-border/60 p-4" data-testid="edu-editor">
              <div className="mb-3 flex items-center justify-between">
                <span className="overline text-muted-foreground">Institution {i + 1}</span>
                <button onClick={() => set("education", p.education.filter((_, j) => j !== i))} className="text-destructive hover:opacity-70"><Trash size={16} /></button>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input placeholder="Institution *" value={e.institution} onChange={(ev) => setEdu(i, "institution", ev.target.value)} data-testid="edu-institution" />
                <Input placeholder="Degree (B.Tech)" value={e.degree} onChange={(ev) => setEdu(i, "degree", ev.target.value)} />
                <Input placeholder="Department" value={e.department} onChange={(ev) => setEdu(i, "department", ev.target.value)} />
                <Input placeholder="Course" value={e.course} onChange={(ev) => setEdu(i, "course", ev.target.value)} />
                <Input placeholder="Class (CSE-A)" value={e.class_name} onChange={(ev) => setEdu(i, "class_name", ev.target.value)} />
                <Input placeholder="Batch (2011-2015)" value={e.batch} onChange={(ev) => setEdu(i, "batch", ev.target.value)} />
                <Input placeholder="Start year" value={e.start_year} onChange={(ev) => setEdu(i, "start_year", ev.target.value)} />
                <Input placeholder="End year" value={e.end_year} onChange={(ev) => setEdu(i, "end_year", ev.target.value)} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Environmental & Research sharing (NaturePulse partnership) */}
      <Card className="mt-6 p-6" data-testid="nature-consent-card">
        <div className="flex items-center gap-2">
          <GraduationCap size={18} className="text-secondary" />
          <h2 className="font-heading text-lg font-medium">Environmental &amp; Research sharing</h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Opt in to plug into the NaturePulse &amp; FarmersPulse citizen-science network. You control each purpose separately and can turn it off anytime.</p>
        <div className="mt-4 space-y-4">
          {NATURE_CONSENTS.map((c) => (
            <div key={c.key} className="flex items-start justify-between gap-4">
              <div>
                <Label className="text-sm font-medium">{c.label}</Label>
                <p className="text-xs text-muted-foreground">{c.desc}</p>
              </div>
              <Switch
                checked={!!(p.nature_consent || {})[c.key]}
                onCheckedChange={(v) => setNature(c.key, v)}
                data-testid={`nature-consent-${c.key}`}
              />
            </div>
          ))}
        </div>
      </Card>

      <div className="sticky bottom-4 mt-6 flex justify-end">
        <Button size="lg" className="rounded-full gap-2 shadow-lg" onClick={save} disabled={saving} data-testid="save-profile-btn">
          <FloppyDisk size={18} /> {saving ? "Saving…" : "Save profile"}
        </Button>
      </div>
    </main>
  );
}
